import React, { useState, useEffect, useRef, useMemo } from 'react';
import { View, Image, StyleSheet, ActivityIndicator, Alert, Pressable, FlatList, Dimensions, LayoutChangeEvent, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { PageContainer, Text, Button, Theme, Logo, OnboardingPageContainer } from '../../components/ui';
import { auth, signInAnonymously } from '../../config/firebase';
import { useTranslation } from 'react-i18next';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');
const MAX_ONBOARDING_WIDTH = 500;

// Calculate responsive image height based on screen size
// On smaller screens (< 700px height), use smaller image
const getImageHeight = () => {
  if (SCREEN_HEIGHT < 700) {
    return 180; // Smaller for compact screens
  } else if (SCREEN_HEIGHT < 800) {
    return 220; // Medium
  }
  return 260; // Default for larger screens
};

interface SlideConfig {
  image: any;
  title: string;
  subtitle: string;
  cta: string;
}

// Static image assets for each slide
const slideImages = [
  require('../../assets/onboarding/intro/intro-1.webp'),
  require('../../assets/onboarding/intro/intro-2.webp'),
  require('../../assets/onboarding/intro/intro-3.webp'),
];

const IntroSlidesScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [logoClicks, setLogoClicks] = useState(0);
  const [containerWidth, setContainerWidth] = useState(MAX_ONBOARDING_WIDTH);
  const flatListRef = useRef<FlatList>(null);

  const handleLogoClick = () => {
    const newClicks = logoClicks + 1;
    setLogoClicks(newClicks);
    
    if (newClicks >= 7) {
      setLogoClicks(0);
      (navigation as any).navigate('Debug');
    }
  };

  // Get slides from translations (A/B test removed; always use "control_improved")
  const slides = useMemo(() => {
    const slidesData = t('intro.slides', { returnObjects: true }) as any;
    const variantSlides = slidesData['control_improved'];
    
    return variantSlides.map((slide: any, idx: number) => ({
      ...slide,
      image: slideImages[idx],
    }));
  }, [t]);

  const isLast = index >= slides.length - 1;
  const current = slides[index];

  // Authenticate anonymously on mount (only if no user or user is already anonymous)
  useEffect(() => {
    const initialize = async () => {
      const currentUser = auth.currentUser;
      
      if (currentUser) {
        // If user is already authenticated (including if they're already anonymous), just proceed
        console.log('User already authenticated:', { 
          isAnonymous: currentUser.isAnonymous,
          email: currentUser.email 
        });
        setIsAuthenticated(true);
      } else {
        // No user at all - sign in anonymously for the intro experience
        setIsAuthenticating(true);
        try {
          // Use modular API for both web and native
          await signInAnonymously(auth);
          console.log('User signed in anonymously for onboarding');
          setIsAuthenticated(true);
        } catch (error: any) {
          console.error('Anonymous sign in error:', error);
          Alert.alert(t('intro.alerts.error'), t('intro.alerts.failedToInitialize'));
        } finally {
          setIsAuthenticating(false);
        }
      }
    };

    initialize();
  }, [t]);

  const handleGetStarted = async () => {
    if (!isAuthenticated) {
      Alert.alert(t('intro.alerts.pleaseWait'), t('intro.alerts.initializing'));
      return;
    }
    
    // Navigate directly to discovery onboarding
    (navigation as any).replace('DiscoveryV2Onboarding');
  };

  const handleSignIn = () => {
    (navigation as any).navigate('Auth', { screen: 'Login' });
  };

  const handleNext = () => {
    if (isLast) {
      return;
    }
    const nextIndex = index + 1;
    setIndex(nextIndex); // Update state immediately for dots
    // Use scrollToOffset instead of scrollToIndex for more reliable scrolling
    flatListRef.current?.scrollToOffset({ 
      offset: nextIndex * containerWidth, 
      animated: true 
    });
  };

  const onViewableItemsChanged = useRef(({ viewableItems }: any) => {
    if (viewableItems.length > 0) {
      setIndex(viewableItems[0].index || 0);
    }
  }).current;

  const renderSlide = ({ item }: { item: SlideConfig }) => (
    <View style={[styles.slide, { width: containerWidth }]}>
      <View style={styles.imageWrapper}>
        <Image source={item.image} style={styles.image} resizeMode="contain" />
      </View>
      <View style={styles.textBlock}>
        <Text variant="heading2" align="center" style={styles.title}>{item.title}</Text>
        <Text variant="body1" align="center" color="secondary" style={styles.subtitle}>{item.subtitle}</Text>
      </View>
    </View>
  );

  return (
    <PageContainer>
      <OnboardingPageContainer onLayout={(width) => setContainerWidth(width)}>
        {/* Header with logo */}
        <View style={styles.header}>
          <Pressable onPress={handleLogoClick} style={styles.logoContainer}>
            <Logo size={22} />
            <Text style={styles.logoText}>{t('intro.logoText')}</Text>
          </Pressable>
        </View>

        <View style={styles.container}>
        {/* Swipeable FlatList */}
        <FlatList
          ref={flatListRef}
          data={slides}
          renderItem={renderSlide}
          keyExtractor={(_, idx) => `slide-${idx}`}
          horizontal
          pagingEnabled
          snapToAlignment="center"
          showsHorizontalScrollIndicator={false}
          onViewableItemsChanged={onViewableItemsChanged}
          viewabilityConfig={{ itemVisiblePercentThreshold: 50 }}
          getItemLayout={(_data, index) => ({
            length: containerWidth,
            offset: containerWidth * index,
            index,
          })}
          style={styles.flatList}
        />

        {/* Dots */}
        <View style={styles.dotsRow}>
          {slides.map((_slide: SlideConfig, i: number) => {
            const isActive = i === index;
            return (
              <View
                key={`${i}-${isActive}`}
                style={[
                  styles.dot,
                  isActive ? styles.dotActive : styles.dotInactive
                ]}
              />
            );
          })}
        </View>

        {/* Buttons */}
        <View style={styles.buttonContainer}>
          {isAuthenticating ? (
            <ActivityIndicator size="large" color={Theme.colors.primary} />
          ) : isLast ? (
            <View style={styles.buttonGroup}>
              <Button 
                title={t('intro.buttons.getStarted')}
                onPress={handleGetStarted} 
                style={styles.cta}
                disabled={!isAuthenticated}
              />
              <Button 
                title={t('intro.buttons.alreadyHaveAccount')}
                variant="secondary"
                onPress={handleSignIn} 
                style={styles.secondaryButton}
              />
            </View>
          ) : (
            <Button title={current.cta} onPress={handleNext} style={styles.cta} />
          )}
        </View>
      </View>
      </OnboardingPageContainer>
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: Theme.spacing.lg,
    paddingTop: SCREEN_HEIGHT < 700 ? Theme.spacing.xs : Theme.spacing.md,
    paddingBottom: SCREEN_HEIGHT < 700 ? Theme.spacing.sm : Theme.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: Theme.colors.border,
    backgroundColor: Theme.colors.surface,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.xs,
  },
  logoText: {
    fontSize: 24,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textPrimary,
  },
  container: {
    flex: 1,
  },
  flatList: {
    flexGrow: 0,
  },
  slide: {
    // width is set dynamically based on container width
    paddingHorizontal: Theme.spacing.xl,
    paddingTop: SCREEN_HEIGHT < 700 ? Theme.spacing.md : Theme.spacing.lg,
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  imageWrapper: {
    alignItems: 'center',
    marginBottom: SCREEN_HEIGHT < 700 ? Theme.spacing.md : Theme.spacing.lg,
    width: '100%',
  },
  image: {
    width: '90%',
    height: getImageHeight(),
  },
  textBlock: {
    paddingHorizontal: Theme.spacing.lg,
    width: '100%',
    alignItems: 'center',
  },
  title: {
    marginBottom: Theme.spacing.sm,
    textAlign: 'center',
  },
  subtitle: {
    textAlign: 'center',
  },
  dotsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: Theme.spacing.sm,
    paddingVertical: Theme.spacing.lg,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  dotActive: {
    backgroundColor: Theme.colors.primary,
    width: 24,
    borderRadius: 5,
  },
  dotInactive: {
    backgroundColor: Theme.colors.border,
    opacity: 0.4,
  },
  buttonContainer: {
    paddingHorizontal: Theme.spacing.lg,
    paddingBottom: Theme.spacing.xl,
  },
  cta: {
    alignSelf: 'stretch',
  },
  buttonGroup: {
    gap: Theme.spacing.md,
  },
  secondaryButton: {
    alignSelf: 'stretch',
  },
});

export default IntroSlidesScreen;
